/**
 * Selector del tema: claro, oscuro o el del sistema.
 *
 * Dos formas del mismo control. `ThemeSwitcher` es el segmentado de tres
 * opciones (con texto en el menu, solo iconos en el login). `ThemeCycleButton`
 * es un solo boton que pasa a la opcion siguiente, para el menu contraido a
 * iconos, donde no caben tres; su nombre dice cual esta activa y a cual pasa.
 *
 * Los nombres accesibles dicen "Tema ..." y nunca "Sistema" a secas: esa
 * palabra ya es el filtro de sistema externo de los listados.
 */
import type { ComponentType, ReactElement } from 'react';
import { THEME_PREFERENCES, useTheme } from '../app/theme';
import type { ThemePreference } from '../app/theme';
import { IconMonitor, IconMoon, IconSun } from './icons';
import type { IconProps } from './icons';

interface ThemeOption {
  /** Texto visible en el segmentado. */
  readonly label: string;
  /** Nombre accesible del boton de la opcion. */
  readonly name: string;
  readonly Icon: ComponentType<IconProps>;
}

const OPTIONS: Readonly<Record<ThemePreference, ThemeOption>> = {
  light: { label: 'Claro', name: 'Tema claro', Icon: IconSun },
  dark: { label: 'Oscuro', name: 'Tema oscuro', Icon: IconMoon },
  system: { label: 'Sistema', name: 'Tema del sistema', Icon: IconMonitor },
};

/** Orden del boton que cicla. */
const NEXT: Readonly<Record<ThemePreference, ThemePreference>> = {
  light: 'dark',
  dark: 'system',
  system: 'light',
};

function joinClasses(...names: readonly (string | false | undefined)[]): string {
  return names.filter((name) => typeof name === 'string' && name !== '').join(' ');
}

export interface ThemeSwitcherProps {
  /** Solo iconos (el nombre de cada opcion queda en el tooltip y para el lector). */
  readonly iconsOnly?: boolean;
  readonly className?: string;
}

export function ThemeSwitcher({ iconsOnly = false, className }: ThemeSwitcherProps): ReactElement {
  const { preference, setPreference } = useTheme();

  return (
    <div
      className={joinClasses('theme-switch', iconsOnly && 'theme-switch--icons', className)}
      role="group"
      aria-label="Tema de la interfaz"
    >
      {THEME_PREFERENCES.map((value) => {
        const option = OPTIONS[value];
        return (
          <button
            key={value}
            type="button"
            className="theme-switch__option"
            aria-label={option.name}
            aria-pressed={preference === value}
            title={option.name}
            onClick={() => setPreference(value)}
          >
            <option.Icon size={15} />
            {iconsOnly ? null : <span className="theme-switch__label">{option.label}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function ThemeCycleButton({ className }: { readonly className?: string }): ReactElement {
  const { preference, setPreference } = useTheme();
  const current = OPTIONS[preference];
  const next = NEXT[preference];
  const Icon = current.Icon;

  return (
    <button
      type="button"
      className={joinClasses('icon-button', className)}
      onClick={() => setPreference(next)}
    >
      <Icon />
      <span className="tip">
        {`Tema: ${current.label.toLowerCase()} · cambiar a ${OPTIONS[next].label.toLowerCase()}`}
      </span>
    </button>
  );
}
